import { Component, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { QuoteService } from 'app/quote/quote.service';
import { MySQLDateToISOPipe } from 'app/shared/mysql-date-to-iso.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { CalendarModule } from 'primeng/calendar';
import { ModalSelectTaskComponent } from '../modal-select-task/modal-select-task.component';
import { ModalSelectWorkOrderComponent } from '../modal-select-work-order/modal-select-work-order.component';

@Component({
	selector: 'modal-timesheet-add-entry',
	templateUrl: './modal-timesheet-add-entry.component.html'
})
export class ModalTimesheetAddEntryComponent extends UIModalBaseComponent {

	@ViewChild(CalendarModule) calendar: CalendarModule;
	@ViewChild(ModalSelectTaskComponent) selectTaskModal: ModalSelectTaskComponent;
	@ViewChild(ModalSelectWorkOrderComponent) selectWorkOrderModal: ModalSelectWorkOrderComponent;

	userId;;
	editdate;
	start_datetime;
	end_datetime;
	task;
	taskselected;
	userselected;
	userList = [];
	workOrder;
	list = null;
	dateString;
	hasConflict = false;
	today: Date = new Date();
	type = 'task';

	constructor(
		public app: AppService,
		private api: ApiService,
		public service: QuoteService
	) {
		super();
	}

	open(mdata) {
		this.userselected = mdata.user_id || null;
		this.taskselected = mdata.task || null;
		this.type = mdata.type || 'task';
		this.list = null;
		this.editdate = null;
		this.start_datetime = null;
		this.end_datetime = null;
		this.userId = this.userselected;
		this.task = this.taskselected;
		this.workOrder = null;
		this.userList = [];

		if (!this.userId) {
			this.api.team.getUserDetails(data => {
				this.userList = data.users;
				this.hasConflict = false;
				this.modal.open();
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		} else {
			this.hasConflict = false;
			this.modal.open();
		}
	}

	onDateChange() {
		this.getTimeSheet();
	}

	onUserChange() {
		this.getTimeSheet();
	}

	editSelectTask() {
		this.selectTaskModal.open({
			has_work_order: 1
		});
	}

	editSelectTaskModalClosed(data) {
		if (data) this.task = data;
	}

	removeTask() {
		this.task = null;
	}

	editSelectWorkOrder() {
		this.selectWorkOrderModal.open({
			mode: 'add'
		});
	}

	editSelectWorkOrderModalClosed(data) {
		if (data) this.workOrder = data;
	}

	removeWorkOrder() {
		this.workOrder = null;
	}

	getTimeSheet() {
		if (!this.editdate) return;
		if (!this.userId) return;

		this.dateString = this.editdate;
		this.api.team.getTimesheetByDate(
			{
				user_id: this.userId,
				date: MySQLDateToISOPipe.dateToString(this.editdate).split(' ')[0],
				needs_approval_only: false
			},
			(data) => {
				if (data.length) {
					data.forEach((item) => {
						item.start_datetime = MySQLDateToISOPipe.stringToDate(item.start_datetime);
						item.end_datetime = MySQLDateToISOPipe.stringToDate(item.end_datetime);
						if (item.breaks) {
							// A break with no end takes nothing off the day and can only be fixed in Edit Times, so it doesn't block adding
							item.breaks = JSON.parse(item.breaks).filter(result => result.end_datetime);
							item.breaks.forEach((result) => {
								result['start_datetime'] = MySQLDateToISOPipe.stringToDate(result['start_datetime']);
								result['end_datetime'] = MySQLDateToISOPipe.stringToDate(result['end_datetime']);
							});
						}
					});
				}
				this.list = data;
			},
			(error) => {
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	add() {
		if (this.type === 'task' && !this.task) {
			this.app.notifications.showDanger('Please select a task');
			return;
		}
		if (this.type === 'travel' && !this.workOrder) {
			this.app.notifications.showDanger('Please select a job');
			return;
		}
		if (!this.start_datetime || !this.end_datetime || !this.editdate || !this.userId) {
			this.app.notifications.showDanger('Enter the values for all fields');
			return;
		}
		this.list = this.list.filter(entry => entry.id !== 'new');
		const newEntry = {
			id: 'new',
			start_datetime: this.start_datetime,
			end_datetime: this.end_datetime,
			breaks: [],
			task_name: this.type === 'task' ? this.task.name : null,
			work_order_no: this.type === 'task' ? this.task.work_order_no : this.workOrder.work_order_no,
			type: this.type
		};
		this.list.push(newEntry);

		this.validateConflicts();
		if (this.hasConflict) {
			this.app.notifications.showDanger('There are conflicts in the timesheet. Please resolve them before adding.');
			return;
		}

		const data = {
			id: 'new',
			user_id: this.userId,
			start_datetime: this.start_datetime,
			end_datetime: this.end_datetime,
			task_id: this.type === 'task' ? this.task.id : null,
			type: this.type,
			work_order_id: this.type === 'task' ? this.task.work_order_id : this.workOrder.id,
			project_id: this.type === 'task' ? this.task.project_id : this.workOrder.project_id,
			is_billable: this.type === 'task' ? this.task.billable : 0
		};

		data.start_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(data.start_datetime));
		data.end_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(data.end_datetime));

		this.api.task.saveTimeEntry(data, () => {
			this.modal.close(data);
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	setDateToEditDate(datetime: string): string {
		const time = new Date(datetime);
		const hours = time.getHours();
		const minutes = time.getMinutes();

		const pad = (num: number) => (num < 10 ? '0' : '') + num;

		return `${MySQLDateToISOPipe.dateToString(this.editdate).split(' ')[0]} ${pad(hours)}:${pad(minutes)}:00`;
	}

	isBreakOutsideTimeEntry(breakStart: Date, breakEnd: Date, entryStart: Date, entryEnd: Date): boolean {
		const [breakStartTime, breakEndTime, entryStartTime, entryEndTime] = [
			this.extractTime(breakStart),
			this.extractTime(breakEnd),
			this.extractTime(entryStart),
			this.extractTime(entryEnd),
		];
		return breakStartTime < entryStartTime || breakEndTime > entryEndTime; // A break outside the entry's time range is a conflict
	}

	isTimeConflict(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
		const [startTime1, endTime1, startTime2, endTime2] = [
			this.extractTime(start1),
			this.extractTime(end1),
			this.extractTime(start2),
			this.extractTime(end2),
		];
		return startTime1 < endTime2 && endTime1 > startTime2; // Checks if two time ranges overlap
	}

	extractTime(date: Date): number {
		return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
	}

	validateConflicts() {
		this.hasConflict = false;

		for (const entry of this.list) {
			entry.isConflict = false;

			if (entry.start_datetime == null || entry.end_datetime == null) {
				entry.isConflict = true;
				this.hasConflict = true;
			} else {
				const entryStartTime = this.extractTime(entry.start_datetime);
				const entryEndTime = this.extractTime(entry.end_datetime);

				// Check if the start time is greater than the end time
				if (entryStartTime > entryEndTime) {
					entry.isConflict = true;
					this.hasConflict = true;
				}

				// Check for time conflicts between entries
				for (let j = 0; j < this.list.length; j++) {
					if (entry.id === this.list[j].id) continue; // Skip self-comparison

					const otherEntry = this.list[j];

					if (otherEntry.start_datetime == null || otherEntry.end_datetime == null) {
						otherEntry.isConflict = true;
						this.hasConflict = true;
					} else if (this.isTimeConflict(entry.start_datetime, entry.end_datetime, otherEntry.start_datetime, otherEntry.end_datetime)) {
						entry.isConflict = true;
						otherEntry.isConflict = true;
						this.hasConflict = true;
					}
				}

				// Check for conflicts within breaks
				if (entry.breaks && entry.breaks.length) {
					for (const brk of entry.breaks) {
						brk.isConflict = false;

						if (brk.start_datetime == null || brk.end_datetime == null) {
							brk.isConflict = true;
							this.hasConflict = true;
						} else {
							const breakStartTime = this.extractTime(brk.start_datetime);
							const breakEndTime = this.extractTime(brk.end_datetime);

							// Check if the break's start time is greater than its end time
							if (breakStartTime > breakEndTime) {
								brk.isConflict = true;
								entry.isConflict = true;
								this.hasConflict = true;
							}

							// Check if the break is outside the entry's time range
							if (this.isBreakOutsideTimeEntry(brk.start_datetime, brk.end_datetime, entry.start_datetime, entry.end_datetime)) {
								brk.isConflict = true;
								entry.isConflict = true;
								this.hasConflict = true;
							}

							// Check for conflicts between breaks within the same entry
							for (const otherBreak of entry.breaks) {
								if (brk.id === otherBreak.id) continue; // Skip self-comparison

								if (otherBreak.start_datetime == null || otherBreak.end_datetime == null) {
									otherBreak.isConflict = true;
									this.hasConflict = true;
								} else if (this.isTimeConflict(brk.start_datetime, brk.end_datetime, otherBreak.start_datetime, otherBreak.end_datetime)) {
									brk.isConflict = true;
									otherBreak.isConflict = true;
									this.hasConflict = true;
								}
							}
						}
					}
				}
			}
		}
	}

	calculateTimeDifference(start_datetime, end_datetime): number | null {
		let timeDifference = null;

		if (start_datetime && end_datetime) {
			const start = new Date(start_datetime);
			const end = new Date(end_datetime);

			const startTime = start.getHours() * 3600 + start.getMinutes() * 60 + start.getSeconds();
			const endTime = end.getHours() * 3600 + end.getMinutes() * 60 + end.getSeconds();

			const differenceInSeconds = endTime - startTime;

			if (differenceInSeconds > 0) {
				timeDifference = differenceInSeconds / 3600;
			} else {
				timeDifference = null;
			}
		} else {
			timeDifference = null;
		}

		return timeDifference;
	}

	conflictMessage(entry) {
		if (entry.isConflict) {
			if (entry.start_datetime == null) return 'Start time empty';
			if (entry.end_datetime == null) return 'End time empty';
			return 'Overlapping';
		}
	}
}
